import { useState } from "react";
import { Loader2, ArrowLeft } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/modules/shared/ui/components/card";
import { Button } from "@/modules/shared/ui/components/button";
import { useSubscriptionStatus } from "../../hooks/use-subscription-status";
import { usePaymentHistory } from "../../hooks/use-payment-history";
import { useSubscription } from "../../hooks/use-subscription";
import { SubscriptionCard } from "../components/SubscriptionCard";
import { PaymentHistoryTable } from "../components/PaymentHistoryTable";
import type { PaymentHistoryDto } from "../../dtos/payment-history.dto";
import { useNavigate } from "react-router-dom";
import ActivateSubscriptionModal from "../components/ActivateSubscriptionModal";
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations";

export function BillingDashboard() {
  const navigate = useNavigate();
  const { data: status, isLoading: statusLoading } = useSubscriptionStatus();
  const { cancel } = useSubscription();
  const currentOrganization = useCurrentOrganization();

  const [selectedPayment, setSelectedPayment] = useState<PaymentHistoryDto | null>(null);
  const [showActivateModal, setShowActivateModal] = useState(false);

  const { data: payments, isLoading: paymentsLoading } = usePaymentHistory(
    status?.subscriptionId
  );

  const handleCancel = async () => {
    if (!status?.subscriptionId) return;
        await cancel({ id: status.subscriptionId, immediate: false });
  };

  const handleSelectPayment = (payment: PaymentHistoryDto) => {
    setSelectedPayment(payment);
  };

  const handleBackToPayments = () => {
    setSelectedPayment(null);
  };

  if (statusLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!status || !status.hasSubscription) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold">No Active Subscription</h1>
          <p className="text-muted-foreground">
            Subscribe to a plan to access all features
          </p>
          <Button
            onClick={() => navigate("/create-subscription")}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5"
          >
            Create Subscription
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Billing & Subscription</h1>
      </div>

      {/* Subscription Card */}
      {status.hasSubscription && status.status !== "Active" && status.status !== "Trial" && (
        <div className="flex justify-end">
          <Button
            onClick={() => setShowActivateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2.5"
          >
            Activate Subscription
          </Button>
        </div>
      )}

      <SubscriptionCard
        subscription={status}
        onCancel={handleCancel}
      />

        {/* Payment History or Payment Details */}
        <div>
          {paymentsLoading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : selectedPayment ? (
            // Single Payment Details View
            <Card className="rounded-2xl border border-border shadow-lg bg-card">
              <CardHeader>
                <div className="flex items-center gap-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToPayments}
                    className="w-fit pl-0 hover:text-blue-600 text-muted-foreground transition-all duration-200"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Payments
                  </Button>
                  <CardTitle className="text-lg font-semibold text-foreground">
                    {selectedPayment.type} — {selectedPayment.status}
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Amount</p>
                    <p className="mt-1.5 text-base font-semibold text-foreground">
                      {selectedPayment.currency} {selectedPayment.totalAmount.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">VAT Amount</p>
                    <p className="mt-1.5 text-base font-semibold text-foreground">
                      {selectedPayment.currency} {selectedPayment.vatAmount.toFixed(2)}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date Created</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {new Date(selectedPayment.createdAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Date Paid</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {selectedPayment.paidAt
                        ? new Date(selectedPayment.paidAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Billing Period</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">
                      {new Date(selectedPayment.periodStart).toLocaleDateString("en-ZA")} —{" "}
                      {new Date(selectedPayment.periodEnd).toLocaleDateString("en-ZA")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Status</p>
                    <p className="mt-1.5 text-sm text-muted-foreground">{selectedPayment.status}</p>
                  </div>
                  {selectedPayment.failureReason && (
                    <div className="col-span-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Failure Reason</p>
                      <p className="mt-1.5 text-sm font-medium text-red-600">
                        {selectedPayment.failureReason}
                      </p>
                    </div>
                  )}
                  {selectedPayment.retryCount > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Retry Count</p>
                      <p className="mt-1.5 text-sm text-muted-foreground">{selectedPayment.retryCount}</p>
                    </div>
                  )}
                  {selectedPayment.nextRetryAt && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Next Retry At</p>
                      <p className="mt-1.5 text-sm text-muted-foreground">
                        {new Date(selectedPayment.nextRetryAt).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : payments && payments.length > 0 ? (

            <div>
              <PaymentHistoryTable
                payments={payments}
                onSelectPayment={handleSelectPayment}
              />
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              No payment history found
            </div>
          )}
        </div>
      <ActivateSubscriptionModal
        isOpen={showActivateModal}
        onClose={() => setShowActivateModal(false)}
        organization={currentOrganization}
      />
    </div>
  );
}